import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAdminTraffic } from '../../../services/adminApi';
import { BarList, Delta, Kpi, PeriodSwitch } from './statsParts';
import TrafficDetail, { FILTRES } from './TrafficDetail';
import '../catalog/catalog.css';
import '../books/people.css';
import './stats.css';

// Trafic : qui vient, d'où, avec quoi. Aucune donnée personnelle (ni adresse IP, ni identifiant) : des totaux. Les « visiteurs » sont des
// visites d'un jour (l'empreinte change chaque jour) ; la durée est le temps actif des visites d'au moins deux pages.

const formatDuree = (secondes) => {
  const s = Math.max(0, Math.round(secondes));
  return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')}`;
};

// Deux vues : la vue d'ensemble (totaux sur une période) et le détail d'une journée, visite par visite (?vue=detail).
function ViewSwitch({ value, onChange }) {
  const { t } = useTranslation('admin');
  return (
    <div className="adm-seg adm-view-switch" role="group" aria-label={t('stats.detail.views')}>
      {['ensemble', 'detail'].map((v) => (
        <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}>{t(`stats.detail.view.${v}`)}</button>
      ))}
    </div>
  );
}

export default function TrafficSection() {
  const [params, setParams] = useSearchParams();
  const vue = params.get('vue') === 'detail' ? 'detail' : 'ensemble';
  const changerVue = (v) => setParams(v === 'detail' ? { vue: 'detail' } : {}, { replace: true });
  if (vue === 'detail') {
    const jour = /^\d{4}-\d{2}-\d{2}$/.test(params.get('jour') || '') ? params.get('jour') : '';
    const filtre = FILTRES.includes(params.get('voir')) ? params.get('voir') : 'personnes';
    const changer = (modif) => {
      const suivant = { vue: 'detail', jour, voir: filtre, ...modif };
      setParams(Object.fromEntries(Object.entries(suivant).filter(([k, val]) => val && !(k === 'voir' && val === 'personnes'))), { replace: true });
    };
    return (<><ViewSwitch value={vue} onChange={changerVue} /><TrafficDetail jour={jour} filtre={filtre} onChange={changer} /></>);
  }
  return (<><ViewSwitch value={vue} onChange={changerVue} /><TrafficOverview /></>);
}

function TrafficOverview() {
  const { t, i18n } = useTranslation('admin');
  const [params, setParams] = useSearchParams();
  const jours = [1, 7, 30].includes(Number(params.get('jours'))) ? Number(params.get('jours')) : 7;
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const derniere = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++derniere.current;
    try {
      const r = await getAdminTraffic({ jours });
      if (numero === derniere.current) { setData(r); setErreur(''); }
    } catch (err) {
      if (numero === derniere.current) setErreur(err?.message || t('stats.loadFailed'));
    }
  }, [jours, t]);
  useEffect(() => { setData(null); charger(); }, [charger]);

  let noms = null;
  try { noms = new Intl.DisplayNames([i18n.language], { type: 'region' }); } catch (_e) { noms = null; }
  const nomDuPays = (code) => {
    if (!code || code === '—') return t('stats.unknown');
    try { return (noms && noms.of(code)) || code; } catch (_e) { return code; }
  };
  const lignes = (liste, libelle) => liste.map((l) => ({ ...l, label: libelle(l.key) }));

  return (
    <>
      <div className="adm-actions">
        <PeriodSwitch value={jours} onChange={(p) => setParams(p === 7 ? {} : { jours: String(p) }, { replace: true })} />
        <span className="adm-note">{t('stats.vsPrevious', { days: jours })}</span>
      </div>

      {erreur && <p className="adm-error" role="alert">{erreur}</p>}
      {!data && !erreur && <p className="adm-state" role="status">{t('catalog.common.loading')}</p>}

      {data && (
        <>
          <div className="adm-kpis">
            <Kpi label={t('stats.traffic.visitors')} value={data.visitors.value}><Delta valeur={data.visitors.value} precedent={data.visitors.previous} /></Kpi>
            <Kpi label={t('stats.traffic.duration')} value={data.averageDurationSeconds.value ? formatDuree(data.averageDurationSeconds.value) : '—'}>
              <small>{data.averageDurationSeconds.value ? t('stats.traffic.durationNote') : t('stats.traffic.durationNone')}</small>
            </Kpi>
          </div>

          <div className="adm-cols adm-cols-eq">
            <section className="adm-card"><h2>{t('stats.traffic.countries')}</h2><BarList rows={lignes(data.countries, nomDuPays)} empty={t('stats.traffic.noData')} /></section>
            <section className="adm-card"><h2>{t('stats.traffic.sources')}</h2><BarList rows={lignes(data.sources, (k) => (k.startsWith('recherche:') ? t('stats.sources.searchEngine', { name: k.slice(10).charAt(0).toUpperCase() + k.slice(11) }) : t(`stats.sources.${k}`, { defaultValue: k })))} empty={t('stats.traffic.noData')} /></section>
            <section className="adm-card"><h2>{t('stats.traffic.devices')}</h2><BarList rows={lignes(data.devices, (k) => (k === '—' ? t('stats.unknown') : t(`stats.devices.${k}`, { defaultValue: k })))} empty={t('stats.traffic.noData')} /></section>
            <section className="adm-card"><h2>{t('stats.traffic.entryPages')}</h2><BarList rows={lignes(data.entryPages, (k) => k)} empty={t('stats.traffic.noData')} /></section>
          </div>

          <p className="adm-note">{t('stats.traffic.rules')}</p>
          {data.truncated && <p className="adm-note">{t('stats.truncated')}</p>}
        </>
      )}
    </>
  );
}
